import { formatIncludedList } from "./included-list";
import type { ListingData } from "./listing-section-data";

export type FeeInclusions =
    | { source: "listing" | "provider"; items: string[] }
    | { source: "unknown"; items: undefined };

export function listingFeeInclusions({
    listingIncluded,
    providerIncluded,
}: {
    listingIncluded: string[] | null | undefined;
    providerIncluded: string[] | null | undefined;
}): FeeInclusions {
    if (listingIncluded?.length)
        return { source: "listing", items: listingIncluded };
    if (providerIncluded?.length)
        return { source: "provider", items: providerIncluded };
    return { source: "unknown", items: undefined };
}

type ListingContactTopic = "fee" | "meeting";

type ListingContactAnswer = {
    question: string;
    answer: string;
    provenance?: string;
    followUpQuestion: string;
};

export function listingContactAnswers({
    data,
    inclusions,
}: {
    data: ListingData;
    inclusions?: FeeInclusions;
}): Record<ListingContactTopic, ListingContactAnswer> {
    const name = data.dogName ?? "this pup";
    const included = formatIncludedList(inclusions?.items);
    const fee = data.priceLabel
        ? `${data.priceLabel} is the listed fee.`
        : "No fee is listed.";
    const includedAnswer = included
        ? inclusions?.source === "provider"
            ? `The provider's fee information lists ${included}. Check whether these inclusions apply to ${name}.`
            : `The listing says the fee includes ${included}.`
        : "The listing doesn't say what it includes.";
    const location = data.city
        ? ` The listed location is ${data.city}${data.distanceLabel ? `, ${data.distanceLabel}` : ""}.`
        : "";
    return {
        fee: {
            question: data.priceLabel
                ? `What's in the ${data.priceLabel}?`
                : "What's included?",
            answer: `${fee} ${includedAnswer}`,
            provenance:
                included && inclusions?.source === "provider"
                    ? "From the provider's fee information"
                    : data.providerName
                      ? `From ${data.providerName}'s listing`
                      : undefined,
            followUpQuestion: data.priceLabel
                ? `What does ${name}'s ${data.priceLabel} fee include?`
                : `What does ${name}'s fee include?`,
        },
        meeting: {
            question: `Can we meet ${name} before deciding?`,
            answer: `The listing doesn't say whether you can meet before deciding.${location}`,
            followUpQuestion: `Can I meet ${name} before deciding?`,
        },
    };
}
